import axios from "axios";
import { useContext, useEffect, useState } from "react";
import { Table, DatePicker, Select, Descriptions, Modal, Spin } from "antd";
import dayjs from "dayjs";
import { AuthContext } from "../../context/AuthContext";

export default function AppliedOnDuty() {
    const { authData, backUrl } = useContext(AuthContext);

    const [requests, setRequests] = useState([]);
    const [loading, setLoading] = useState(false);
    const [isMobile, setIsMobile] = useState(window.innerWidth < 768);

    const [filters, setFilters] = useState({
        status: null,
        month: null,
        year: null,
        fromDate: null,
        toDate: null,
    });

    const [showLead, setShowLead] = useState(false);
    const [selectedLeadDetails, setSelectedLeadDetails] = useState(null);

    useEffect(() => {
        const handleResize = () => setIsMobile(window.innerWidth < 768);
        window.addEventListener("resize", handleResize);
        return () => window.removeEventListener("resize", handleResize);
    }, []);

    const fetchData = async (filterValues = filters) => {
        try {
            setLoading(true);

            const params = {};

            if (filterValues.status) params.status = filterValues.status;
            if (filterValues.month) params.month = filterValues.month;
            if (filterValues.year) params.year = filterValues.year;
            if (filterValues.fromDate)
                params.from_date = dayjs(filterValues.fromDate).format("YYYY-MM-DD");
            if (filterValues.toDate)
                params.to_date = dayjs(filterValues.toDate).format("YYYY-MM-DD");

            const res = await axios.get(
                `${backUrl}/api/hr/attendance/my-onduty-requests/`,
                {
                    headers: {
                        Authorization: `Bearer ${authData?.access}`,
                    },
                    params,
                }
            );

            setRequests(res.data.results || res.data);
        } catch (error) {
            console.error("Error fetching On Duty requests:", error.response || error.message);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchData();
    }, []);

    const updateFilter = (key, value) => {
        const newFilters = { ...filters, [key]: value };
        setFilters(newFilters);
        fetchData(newFilters);
    };

    const statusColors = {
        PENDING: "#faad14",
        APPROVED: "green",
        REJECTED: "red",
    };

    const getStatusColor = (status) => {
        if (!status) return "#faad14";
        return statusColors[status.toUpperCase()] || "#faad14";
    };

    const columns = [
        {
            title: "Employee Name",
            dataIndex: "employee_name",
            key: "employee_name",
        },
        {
            title: "On Duty Type",
            dataIndex: "onduty_type",
            key: "onduty_type",
            render: (text, record) => (
                <>
                    <p>{text}</p>
                    <p>{record.check_in_out_mode}</p>

                    {record?.lead_data && (
                        <a
                            onClick={() => {
                                setSelectedLeadDetails(record.lead_data);
                                setShowLead(true);
                            }}
                            style={{ color: "#1677ff", cursor: "pointer" }}
                        >
                            Lead Details
                        </a>
                    )}
                </>
            ),
        },
        {
            title: "Check-In Photo",
            dataIndex: "photo",
            key: "check_in_photo",
            render: (url) =>
                url ? (
                    <img
                        src={url}
                        alt="Check-In"
                        style={{
                            width: 50,
                            height: 50,
                            objectFit: "cover",
                            borderRadius: 4,
                        }}
                    />
                ) : (
                    "-"
                ),
        },
        {
            title: "Location",
            dataIndex: "location",
            key: "location",
            width: 250,
            render: (text, record) =>
                ((record.check_in_latitude && record.check_in_longitude) ||
                    (record.check_out_latitude && record.check_out_longitude)) ? (
                    <a
                        href={`https://www.google.com/maps?q=${record.check_in_latitude || record.check_out_latitude
                            },${record.check_in_longitude || record.check_out_longitude}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        style={{ color: "#1677ff" }}
                    >
                        {text || "View Location"}
                    </a>
                ) : (
                    text || "-"
                ),
        },
        {
            title: "Start Time",
            dataIndex: "start_time",
            render: (val) => (val ? dayjs(val).format("DD/MM/YYYY hh:mm A") : "-"),
        },
        {
            title: "End Time",
            dataIndex: "end_time",
            render: (val) => (val ? dayjs(val).format("DD/MM/YYYY hh:mm A") : "-"),
        },
        {
            title: "Created At",
            dataIndex: "created_at",
            render: (val) => (val ? dayjs(val).format("DD/MM/YYYY hh:mm A") : "-"),
        },
        {
            title: "Remarks",
            dataIndex: "remarks",
            width: 300,
            render: (text) => (
                <div style={{ whiteSpace: "normal", wordWrap: "break-word" }}>
                    {text || "-"}
                </div>
            ),
        },
        {
            title: "Status",
            dataIndex: "status",
            render: (text) => (
                <span style={{ fontWeight: 500, color: getStatusColor(text) }}>
                    {text
                        ? text.charAt(0).toUpperCase() +
                        text.slice(1).toLowerCase()
                        : "-"}
                </span>
            ),
        },
        {
            title: "Approval Logs",
            key: "approval_logs",
            width: 400,
            render: (_, record) => {
                const logs = record.approval_logs || [];
                if (!logs.length) return "-";

                return (
                    <div style={{ whiteSpace: "normal", wordWrap: "break-word" }}>
                        {logs.map((log, index) => (
                            <div key={index} style={{ marginBottom: 6 }}>
                                <strong>{log.approver_name}</strong> :
                                <span
                                    style={{
                                        color: getStatusColor(log.status),
                                        fontWeight: 500,
                                    }}
                                >
                                    {" "}
                                    {log.status}
                                </span>
                                <br />

                                {log.remarks && (
                                    <>
                                        <span style={{ color: "#666" }}>
                                            Remarks: {log.remarks}
                                        </span>
                                        <br />
                                    </>
                                )}

                                {log.updated_at && (
                                    <small style={{ color: "#999" }}>
                                        {dayjs(log.updated_at).format(
                                            "DD/MM/YYYY hh:mm A"
                                        )}
                                    </small>
                                )}
                            </div>
                        ))}
                    </div>
                );
            },
        },
    ];

    const years = Array.from(
        new Set(requests.map((item) => dayjs(item.start_time).year()))
    ).sort((a, b) => b - a);

    return (
        <>
            {/* Lead Modal */}

            <Modal
                open={showLead}
                footer={null}
                onCancel={() => {
                    setShowLead(false);
                    setSelectedLeadDetails(null);
                }}
                width={700}
            >
                {selectedLeadDetails && (
                    <Descriptions bordered column={1} size="small">
                        <Descriptions.Item label="Lead Number">
                            <strong>
                                {selectedLeadDetails.lead_no || "-"}
                            </strong>
                        </Descriptions.Item>

                        <Descriptions.Item label="Customer Name">
                            {selectedLeadDetails.customer_name || "-"}
                        </Descriptions.Item>

                        <Descriptions.Item label="Email">
                            {selectedLeadDetails.email || "-"}
                        </Descriptions.Item>

                        <Descriptions.Item label="Phone Number">
                            {selectedLeadDetails.number || "-"}
                        </Descriptions.Item>

                        <Descriptions.Item label="Project Name">
                            {selectedLeadDetails.project_name || "-"}
                        </Descriptions.Item>

                        <Descriptions.Item label="Status">
                            {selectedLeadDetails.status || "-"}
                        </Descriptions.Item>
                    </Descriptions>
                )}
            </Modal>

            {/* Filters */}

            <div
                style={{
                    display: "flex",
                    flexWrap: "wrap",
                    justifyContent: "center",
                    gap: "10px",
                    marginBottom: 16,
                    flexDirection: isMobile ? "column" : "row",
                }}
            >
                <Select
                    placeholder="Year"
                    allowClear
                    style={{ width: 140 }}
                    onChange={(val) => updateFilter("year", val)}
                >
                    {years.map((year) => (
                        <Select.Option key={year} value={year}>
                            {year}
                        </Select.Option>
                    ))}
                </Select>

                <Select
                    placeholder="Month"
                    allowClear
                    style={{ width: 160 }}
                    onChange={(val) => updateFilter("month", val)}
                >
                    {dayjs.months().map((month, index) => (
                        <Select.Option key={index} value={index + 1}>
                            {month}
                        </Select.Option>
                    ))}
                </Select>

                <DatePicker
                    placeholder="From Date"
                    format="DD/MM/YYYY"
                    style={{ width: 160 }}
                    onChange={(date) => updateFilter("fromDate", date)}
                />

                <DatePicker
                    placeholder="To Date"
                    format="DD/MM/YYYY"
                    style={{ width: 160 }}
                    onChange={(date) => updateFilter("toDate", date)}
                />

                <Select
                    placeholder="Status"
                    allowClear
                    style={{ width: 200 }}
                    onChange={(val) => updateFilter("status", val)}
                >
                    {["PENDING", "APPROVED", "REJECTED"].map((status) => (
                        <Select.Option key={status} value={status}>
                            {status}
                        </Select.Option>
                    ))}
                </Select>
            </div>

            <Spin spinning={loading} size="large">
                <Table
                    columns={columns}
                    dataSource={requests.map((item, index) => ({
                        key: index,
                        ...item,
                    }))}
                    pagination={{ pageSize: 20 }}
                    scroll={{ x: true }}
                    components={{
                        header: {
                            cell: (props) => (
                                <th
                                    {...props}
                                    style={{
                                        backgroundColor: "#0e497b",
                                        color: "white",
                                        whiteSpace: "nowrap",
                                    }}
                                >
                                    {props.children}
                                </th>
                            ),
                        },
                    }}
                    bordered
                />
            </Spin>
        </>
    );
}